import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Upload, FileText, Send, CircleDot, Circle, AlertTriangle, Download, Info } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, getToken, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const DOC_BADGE = {
  en_attente: { label: "En attente", cls: "bg-amber-100 text-amber-700", Icon: Circle },
  validee: { label: "Validée", cls: "bg-green-100 text-green-700", Icon: Check },
  a_refaire: { label: "À refaire", cls: "bg-red-100 text-red-700", Icon: AlertTriangle },
};

export default function VisaFileDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vf, setVf] = useState(null);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const fileRefs = useRef({});

  const load = () => api.get(`/visa/files/${id}`).then(({ data }) => setVf(data)).catch(() => navigate("/client"));

  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const uploadDoc = async (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/visa/files/${id}/documents/${index}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Pièce envoyée");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    setSending(true);
    try {
      await api.post(`/visa/files/${id}/messages`, { text: msg });
      setMsg("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (!vf) return <div className="min-h-[60vh] flex items-center justify-center"><div className="h-10 w-10 rounded-full border-4 border-accent border-t-transparent animate-spin" /></div>;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <button onClick={() => navigate("/client")} className="flex items-center gap-1 text-sm text-muted-foreground mb-4 hover:text-primary">
        <ArrowLeft className="h-4 w-4" /> Retour
      </button>
      <h1 className="font-head text-3xl font-extrabold text-primary mb-1">{vf.pathway_name}</h1>
      <p className="text-muted-foreground mb-6">Suivi de votre dossier visa</p>

      {/* Timeline */}
      <div className="bg-white rounded-2xl soft-shadow border border-gray-100 p-6 mb-6">
        <h2 className="font-head font-bold text-lg text-primary mb-4">Chronologie</h2>
        <ol className="space-y-1">
          {vf.steps.map((s, i) => {
            const done = i < vf.current_step_index;
            const current = i === vf.current_step_index;
            return (
              <li key={i} className="flex items-start gap-3 py-2" data-testid={`step-${i}`}>
                <div className="flex flex-col items-center">
                  {done ? <Check className="h-5 w-5 text-green-500" /> : current ? <CircleDot className="h-5 w-5 text-accent" /> : <Circle className="h-5 w-5 text-gray-300" />}
                  {i < vf.steps.length - 1 && <div className={`w-0.5 h-5 ${done ? "bg-green-400" : "bg-gray-200"}`} />}
                </div>
                <span className={`text-sm pt-0.5 ${current ? "font-bold text-primary" : done ? "text-primary/70" : "text-muted-foreground"}`}>{s}</span>
              </li>
            );
          })}
        </ol>
      </div>

      {/* Documents */}
      <div className="bg-white rounded-2xl soft-shadow border border-gray-100 p-6 mb-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-head font-bold text-lg text-primary">Pièces à fournir</h2>
        </div>
        <div className="flex items-start gap-2 text-xs text-muted-foreground mb-4 bg-accent/5 rounded-lg p-2">
          <Info className="h-4 w-4 text-accent shrink-0" /> Liste indicative — l'agence confirme les pièces exactes.
        </div>
        <div className="space-y-3">
          {vf.documents.map((d, i) => {
            const badge = DOC_BADGE[d.status];
            return (
              <div key={i} className="border border-gray-100 rounded-xl p-4" data-testid={`doc-${i}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-primary" />
                    <span className="font-semibold text-primary text-sm">{d.name}</span>
                  </div>
                  <span className={`text-xs font-bold px-3 py-1 rounded-full ${badge.cls}`} data-testid={`doc-status-${i}`}>{badge.label}</span>
                </div>
                {d.comment && (
                  <p className="text-xs text-red-600 mt-2 bg-red-50 rounded-lg p-2" data-testid={`doc-comment-${i}`}>Commentaire de l'équipe : {d.comment}</p>
                )}
                <div className="flex items-center gap-3 mt-3">
                  {d.file_id && (
                    <a href={`${API}/files/${d.file_id}/download?auth=${getToken()}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-accent font-semibold" data-testid={`doc-view-${i}`}>
                      <Download className="h-3.5 w-3.5" /> {d.original_filename || "Voir le fichier"}
                    </a>
                  )}
                  <label className="inline-flex items-center gap-1 text-xs font-semibold text-primary cursor-pointer hover:text-accent" data-testid={`doc-upload-label-${i}`}>
                    <Upload className="h-3.5 w-3.5" /> {d.file_id ? "Remplacer" : "Envoyer la pièce"}
                    <input ref={(el) => (fileRefs.current[i] = el)} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => uploadDoc(i, e)} data-testid={`doc-file-input-${i}`} />
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Messages */}
      <div className="bg-white rounded-2xl soft-shadow border border-gray-100 p-6">
        <h2 className="font-head font-bold text-lg text-primary mb-4">Messagerie avec l'équipe</h2>
        <div className="space-y-3 mb-4 max-h-80 overflow-y-auto" data-testid="messages-list">
          {vf.messages.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">Aucun message pour le moment.</p>
          ) : (
            vf.messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender_role === "client" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${m.sender_role === "client" ? "bg-accent text-accent-foreground" : "bg-secondary text-primary"}`}>
                  <div className="text-[11px] opacity-70 mb-0.5">{m.sender_role === "admin" ? "Équipe WORLD TRAVEL" : m.sender_name}</div>
                  <div className="text-sm">{m.text}</div>
                </div>
              </div>
            ))
          )}
        </div>
        <form onSubmit={sendMessage} className="flex gap-2">
          <Input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Écrire un message..." className="rounded-full" data-testid="message-input" />
          <Button type="submit" disabled={sending} className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 px-4" data-testid="send-message-btn">
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}
